import { forwardRef, useImperativeHandle, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import CustomDataGrid from '../../../components/data-grid';
import { Box, IconButton, InputAdornment, TextField } from '@mui/material';
import { EditIcon, searchIcon, crossIcon, trashIcon, kababMenu } from '../../../assets/images/index';
import Dropdown from "../../../components/dropdown/index";
import './student-list.scss'
import Button from '../../../components/button';
import { SearchExerciseService } from "../../../services/search-exercise/search-exercise.service";
import { UserService } from '../../../services/user/user.service';
import CustomTooltip from "../../../components/custom-tooltip/index";


const StudentList = forwardRef(({ fetchStudentCounts }: { fetchStudentCounts?: () => void }, ref) => {
    const { t } = useTranslation();
    const searchExerciseService = new SearchExerciseService();
    const userService = new UserService();

    const [rows, setRows] = useState([]);
    const [rowCount, setRowCount] = useState(0);
    const [page, setPage] = useState(0);
    const [pageSize, setPageSize] = useState(10);
    const [loading, setLoading] = useState(false);

    const [filters, setFilters] = useState({ user_type: '', level: '', student_name: '', teacher: '', school: null });

    const [levelOptions, setLevelOptions] = useState([{ value: '', label: t('all') }]);
    const [orgOptions, setOrgOptions] = useState([{ value: '', label: t('all') }]);
    const [teacherOptions, setTeacherOptions] = useState([{ value: '', label: t('all') }]);

    const userTypeOptions = [
        { value: '', label: t('all') },
        { value: '4', label: t('school') },
        { value: '3', label: t('casualUser') },
    ];

    const fetchLevels = async () => {
        try {
            const levels = await searchExerciseService.getLevels();
            const formatted = levels.map((l: any) => ({ value: l.id.toString(), label: l.name }));
            setLevelOptions([{ value: '', label: t('all') }, ...formatted]);
        } catch (error) {
            console.error("Failed to fetch levels", error);
        }
    };

    const fetchTeachers = async (orgId?: number) => {
        try {
            const teachers = await userService.getTeachers(orgId);
            const formatted = teachers.map((t: any) => ({ value: t.id.toString(), label: `${t.first_name} ${t.last_name}` }));
            setTeacherOptions([{ value: '', label: t('all') }, ...formatted]);
        } catch (error) {
            console.error("Failed to fetch teachers", error);
        }
    };

    const fetchOrganisations = async () => {
        try {
            const orgs = await userService.getOrganizations();
            const formatted = orgs.map((d: any) => ({ value: d.id.toString(), label: d.name }));
            setOrgOptions([{ value: '', label: t('all') }, ...formatted]);
        } catch (error) {
            console.error("Failed to fetch difficulties", error);
        }
    };
    

    const fetchStudentList = async () => {
        setLoading(true);
        try {
            const data = await userService.fetchStudetList(
                page + 1,
                pageSize,
                filters.user_type,
                filters.school,
                filters.teacher,
                filters.level,
                filters.student_name
            );
            const formatted = data.results.map((item: any, index: number) => ({
                id: index + 1,
                first_name: item.first_name,
                last_name: item.last_name,
                email: item.email,
                phone: item.phone || '-',
                user_type: item.user_type === "3" ? t('casualUser') : t("school"), 
                school: item.school || '-',
                teacher: Array.isArray(item.teacher)
                    ? item.teacher.join(', ')
                    : (typeof item.teacher === 'string' ? item.teacher : '-') || '-',
                class: item.class_name || '-',
                level: item.level_name || '-'
            }));

            setRows(formatted);
            setRowCount(data.total);
        } catch (error) {
            console.error("Error fetching student list:", error);
        } finally {
            setLoading(false);
        }
    };

    useImperativeHandle(ref, () => ({
        fetchStudentList
    }));

    useEffect(() => {
        fetchStudentList();
    }, [page, pageSize]);

    useEffect(() => {
        fetchLevels();
        fetchOrganisations();
    }, []);

    useEffect(() => {
        if (filters.school) {
            fetchTeachers(filters.school);
        }
    }, [filters.school]);

    const handleSearchClick = () => {
        setPage(0);
        fetchStudentList();
    };

    const columns = [
        {
            field: 'first_name',
            headerName: t('firstName'),
            flex: 1.5,
            renderCell: (params: any) => <CustomTooltip content={params.value} />
        },
        {
            field: 'last_name',
            headerName: t('lastName'),
            flex: 2,
            renderCell: (params: any) => <CustomTooltip content={params.value} />
        },
        {
            field: 'email',
            headerName: t('email'),
            flex: 3.5,
            cellClassName: "col-email",
            renderCell: (params: any) => <CustomTooltip content={params.value} />
        },
        {
            field: 'phone',
            headerName: t('phone'),
            flex: 2.1,
            cellClassName: "col-phone",
            renderCell: (params: any) => <CustomTooltip content={params.value} />
        },
        {
            field: 'user_type',
            headerName: t('userType'),
            flex: 2,
            renderCell: (params: any) => <CustomTooltip content={params.value} />
        },
        {
            field: 'school',
            headerName: t('school'),
            flex: 2,
            renderCell: (params: any) => <CustomTooltip content={params.value} />
        },
        {
            field: 'teacher',
            headerName: t('teacher'),
            flex: 2,
            renderCell: (params: any) => {
                const fullValue = params.value || '-';
                const displayValue = fullValue.includes(',')
                    ? `${fullValue.split(',')[0]}...`
                    : fullValue;
        
                return <CustomTooltip content={fullValue} displayContent={displayValue} />;
            },     
        },
        {
            field: 'class',
            headerName: t('class'),
            flex: 2,
            renderCell: (params: any) => <CustomTooltip content={params.value} />
        },
        {
            field: 'level',
            headerName: t('studyUnit'),
            flex: 2,
            renderCell: (params: any) => <CustomTooltip content={params.value} />
        },
        {
            field: 'actions',
            headerName: t('operations'),
            sortable: false,
            flex: 1,
            renderCell: (params: any) => (
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', height: '100%' }}>
                    <IconButton className='action_btns download_btn'>
                        <img src={kababMenu} alt="Edit" />
                    </IconButton>
                </div>
            )
        }
    ];
    const handleFilterChange = (key: string, value: string) => {
        setFilters(prev => ({ ...prev, [key]: value }));
    };

    const handleRemoveFilter = (key: string) => {
        setFilters(prev => ({ ...prev, [key]: '' }));
    };

    const handleClearAllFilters = () => {
        setFilters({ user_type: '', level: '', student_name: '', teacher: '', school: null });
    };

    const getActiveFilters = () => {
        const active: { key: string, label: string, value: string }[] = [];

        if (filters.user_type) {
            const label = userTypeOptions.find(opt => opt.value === filters.user_type)?.label || filters.user_type;
            active.push({ key: 'user_type', label: t('userType'), value: label });
        }
        if (filters.level) {
            const label = levelOptions.find(opt => opt.value === filters.level)?.label || filters.level;
            active.push({ key: 'level', label: t('level'), value: label });
        }
        if (filters.school) {
            const label = orgOptions.find(opt => opt.value === filters.school)?.label || filters.school;
            active.push({ key: 'school', label: t('school'), value: label });
        }
        if (filters.teacher) {
            const label = teacherOptions.find(opt => opt.value === filters.teacher)?.label || filters.teacher;
            active.push({ key: 'teacher', label: t('teacher'), value: label });
        }

        return active;
    };

    const activeFilters = getActiveFilters();

    return (
        <div className='dashboard-table'>
            <Box className='search_exercise'>
                <Dropdown
                    label={t('userType')}
                    value={filters.user_type}
                    options={userTypeOptions}
                    onChange={(value) => handleFilterChange('user_type', value)}
                />
                <Dropdown
                    label={t('school')}
                    value={filters.school}
                    options={orgOptions}
                    onChange={(value) => handleFilterChange('school', value)}
                />
                <Dropdown
                    label={t('teacher')}
                    value={filters.teacher}
                    options={teacherOptions}
                    onChange={(value) => handleFilterChange('teacher', value)}
                />
                <Dropdown
                    label={t('level')}
                    value={filters.level}
                    options={levelOptions}
                    onChange={(value) => handleFilterChange('level', value)}
                />
                <TextField
                    label={t('studentSearch')}
                    variant="outlined"
                    size="small"
                    value={filters.student_name}
                    onChange={(e) => handleFilterChange('student_name', e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter') handleSearchClick(); }}
                    InputProps={{
                        endAdornment: (
                            <InputAdornment position="end">
                                <img src={searchIcon} alt="Search" />
                            </InputAdornment>
                        )
                    }}
                />
                <Button text={t('search')} color='blue' onClick={handleSearchClick} />
            </Box>

            {activeFilters.length > 0 && (
                <div className='table_header'>
                    <p className='filter_result_count'>
                        <span>{rowCount}</span> {t('resultsFound')}
                    </p>
                    <div className='table_header_button'>
                        {activeFilters.map((filter) => (
                            <div className='single_header_button' key={filter.key}>
                                <span>{filter.label}:</span>
                                <ul>
                                    <li>
                                        <span>{filter.value}</span>
                                        <button onClick={() => handleRemoveFilter(filter.key)}>
                                            <img src={crossIcon} alt="Cross Icon" />
                                        </button>
                                    </li>
                                </ul>
                            </div>
                        ))}
                        <button className='delete_filter_btn' onClick={handleClearAllFilters}>
                                <img src={trashIcon} alt="Trash Icon" />
                            <span>{t('deleteFilters')}</span>
                        </button>
                    </div>
                </div>
            )}

            <CustomDataGrid
                rows={rows}
                columns={columns}
                rowCount={rowCount}
                page={page}
                pageSize={pageSize}
                loading={loading}
                onPageChange={setPage}
                onPageSizeChange={setPageSize}
            />
        </div>
    );
});


export default StudentList;
